'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useAuth } from '@/components/auth-provider';
import { useConsent } from '@/hooks/use-consent';
import { getAnalyticsConsent } from '@/lib/consent';
import { attributionWriteAction } from '@/lib/legal/consent-gate';
import { tracker } from '@/lib/tracking';

const COOKIE_NAME = 'referralCode';
const COOKIE_DAYS = 30;
const LS_KEY = 'robutler_ref';
const LS_TS_KEY = 'robutler_ref_ts';
const ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// First-touch sentinel — once set, we never overwrite the first-touch row.
// last-touch fires on every campaign-tagged visit; signup-touch fires from the
// post-signup callback (not from this component — see useAuth() in AuthProvider).
const FIRST_TOUCH_SENTINEL = 'rb_first_touch';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

const GCLID_LS_KEY = 'rb_gclid';
const GCLID_LS_TS_KEY = 'rb_gclid_ts';
const GCLID_COOKIE = 'rb_gclid';
const GCLID_DAYS = 90; // Google's attribution window
const GCLID_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;
const AD_CLICK_PARAMS = ['gclid', 'wbraid', 'gbraid'] as const;

/* ------------------------------------------------------------------ */
/*  Cookie helpers                                                     */
/* ------------------------------------------------------------------ */

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; Expires=${expires}; Path=/; SameSite=Lax; Secure`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

/* ------------------------------------------------------------------ */
/*  Consent gate + sessionStorage staging                              */
/* ------------------------------------------------------------------ */

// The referralCode and rb_gclid cookies (and their localStorage mirrors) are
// non-essential attribution storage, so they are written only after cookie
// consent (lib/consent). While consent is undecided, captured values are
// staged in sessionStorage and promoted to the real cookies if the user
// accepts; if the user declines, nothing is written and staging is cleared.
const STAGE_REF_KEY = 'rb_stage_ref';
const STAGE_GCLID_KEY = 'rb_stage_gclid';
/** Backstop poll for a consent decision made outside this component. */
const CONSENT_POLL_MS = 250;

function stageValue(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage unavailable — the capture is simply lost until consent
  }
}

function clearStagedAttribution() {
  try {
    sessionStorage.removeItem(STAGE_REF_KEY);
    sessionStorage.removeItem(STAGE_GCLID_KEY);
  } catch {
    // ignore
  }
}

/**
 * Promote sessionStorage-staged captures into the real cookies + localStorage
 * once consent is accepted (storeReferral / storeAdClickId re-check consent,
 * so this is a no-op unless consent is 'accepted').
 *
 * Exported so the consent banner can call it SYNCHRONOUSLY from its accept
 * handler. The polling backstop below runs on an interval, and a visitor who
 * accepts and immediately clicks "Sign up" can leave the origin before the
 * next tick, which loses the referral cookie the OAuth callback reads.
 */
export function promoteStagedAttribution() {
  if (getAnalyticsConsent() !== 'accepted') return;
  try {
    const ref = sessionStorage.getItem(STAGE_REF_KEY);
    if (ref) storeReferral(ref);
    const gclidRaw = sessionStorage.getItem(STAGE_GCLID_KEY);
    if (gclidRaw) {
      const { param, value } = JSON.parse(gclidRaw) as { param: string; value: string };
      if (param && value) storeAdClickId(param, value);
    }
  } catch {
    // ignore
  }
  clearStagedAttribution();
}

/* ------------------------------------------------------------------ */
/*  localStorage helpers (last-touch with 30-day window)               */
/* ------------------------------------------------------------------ */

// Exported for ShareReferralCapture (/w share views attribute to the content
// owner via the same last-touch cookie + LS mechanics).
// Consent-gated: writes the real cookie + LS only after consent; stages in
// sessionStorage while consent is undecided; drops when consent is declined.
export function storeReferral(code: string) {
  const action = attributionWriteAction(getAnalyticsConsent());
  if (action === 'stage') {
    stageValue(STAGE_REF_KEY, code);
    return;
  }
  if (action === 'drop') {
    clearStagedAttribution();
    return;
  }
  try {
    localStorage.setItem(LS_KEY, code);
    localStorage.setItem(LS_TS_KEY, Date.now().toString());
  } catch {
    // localStorage unavailable (private browsing etc.)
  }
  // Also set cookie so server-side auth callbacks can read it
  setCookie(COOKIE_NAME, code, COOKIE_DAYS);
  try {
    sessionStorage.removeItem(STAGE_REF_KEY);
  } catch {
    // ignore
  }
}

function getStoredReferral(): string | null {
  try {
    const code = localStorage.getItem(LS_KEY);
    const ts = localStorage.getItem(LS_TS_KEY);
    if (!code || !ts) return null;

    // Check 30-day attribution window
    const age = Date.now() - Number(ts);
    if (age > ATTRIBUTION_WINDOW_MS) {
      // Expired — clear
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(LS_TS_KEY);
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Google Ads click ID helpers (gclid / wbraid / gbraid)              */
/* ------------------------------------------------------------------ */

// Consent-gated like storeReferral: write / stage / drop per consent state.
function storeAdClickId(param: string, value: string) {
  const action = attributionWriteAction(getAnalyticsConsent());
  if (action === 'stage') {
    stageValue(STAGE_GCLID_KEY, JSON.stringify({ param, value }));
    return;
  }
  if (action === 'drop') {
    clearStagedAttribution();
    return;
  }
  try {
    localStorage.setItem(GCLID_LS_KEY, JSON.stringify({ param, value }));
    localStorage.setItem(GCLID_LS_TS_KEY, Date.now().toString());
  } catch { /* private browsing */ }
  setCookie(GCLID_COOKIE, `${param}:${value}`, GCLID_DAYS);
  try {
    sessionStorage.removeItem(STAGE_GCLID_KEY);
  } catch {
    // ignore
  }
}

function captureAdClickParams(searchParams: URLSearchParams): boolean {
  for (const p of AD_CLICK_PARAMS) {
    const v = searchParams.get(p);
    if (v) {
      storeAdClickId(p, v);
      return true;
    }
  }
  return false;
}

/**
 * Retrieve the stored Google Ads click identifier, if any.
 * Returns { param, value } within the 90-day attribution window, or null.
 */
export function getAdClickId(): { param: string; value: string } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(GCLID_LS_KEY);
    const ts = localStorage.getItem(GCLID_LS_TS_KEY);
    if (!raw || !ts) return null;
    if (Date.now() - Number(ts) > GCLID_WINDOW_MS) {
      localStorage.removeItem(GCLID_LS_KEY);
      localStorage.removeItem(GCLID_LS_TS_KEY);
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/*  URL pattern extraction                                             */
/* ------------------------------------------------------------------ */

// Client-side twin of `isUuid` in lib/content/share-code.ts (that module
// pulls in node `crypto`, so it can't be imported from a client component).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function extractReferralInfo(pathname: string, searchParams: URLSearchParams, hash: string): {
  type: 'user' | 'channel' | 'post' | 'comment' | 'direct' | 'content-share' | 'workspace' | 'content';
  identifier: string;
} | null {
  // 1. Direct referral link: /ref/[code]
  const refMatch = pathname.match(/^\/ref\/([A-Za-z0-9]+)$/);
  if (refMatch) {
    return { type: 'direct', identifier: refMatch[1] };
  }

  // 2. Query parameter: ?ref=[code]
  const refParam = searchParams.get('ref') || searchParams.get('referral') || searchParams.get('refcode');
  if (refParam) {
    return { type: 'direct', identifier: refParam };
  }

  // 3. Content page: /content/[id] (with optional `?t=` share token).
  //    A share token wins because it identifies a specific grant; the
  //    raw `/content/[id]` path falls through to ownership resolution
  //    so a normal "Copy link" share still attributes the owner without
  //    needing `?ref=` glued on.
  const contentMatch = pathname.match(/^\/content\/([^/]+)$/);
  if (contentMatch) {
    const shareToken = searchParams.get('t');
    if (shareToken) {
      return { type: 'content-share', identifier: shareToken };
    }
    return { type: 'content', identifier: contentMatch[1] };
  }

  // 3b. Standalone widget viewer: /w/[idOrCode] (+ /wp/ perf variant) —
  //     the share dialog's primary widget URLs. Mirrors the server-side
  //     disambiguation in lib/widgets/standalone-resolve.ts: a UUID names
  //     the content directly (owner resolution, visibility honoured);
  //     anything else is a short share code stored in
  //     content_links.shareToken. An explicit `?t=` token wins on the
  //     UUID form because it identifies a specific grant.
  const widgetMatch = pathname.match(/^\/wp?\/([^/]+)$/);
  if (widgetMatch) {
    const shareToken = searchParams.get('t');
    if (shareToken) {
      return { type: 'content-share', identifier: shareToken };
    }
    if (UUID_RE.test(widgetMatch[1])) {
      return { type: 'content', identifier: widgetMatch[1] };
    }
    return { type: 'content-share', identifier: widgetMatch[1] };
  }

  // 4. User/Agent profile: /u/[username]
  const userMatch = pathname.match(/^\/u\/([^/]+)$/);
  if (userMatch) {
    return { type: 'user', identifier: userMatch[1] };
  }

  // 4b. Agent page: /agents/[name] (resolves to agent owner)
  const agentMatch = pathname.match(/^\/agents\/([^/]+)$/);
  if (agentMatch) {
    return { type: 'user', identifier: agentMatch[1] };
  }

  // 5. Channel page: /c/[...slug]
  const channelMatch = pathname.match(/^\/c\/(.+)$/);
  if (channelMatch) {
    return { type: 'channel', identifier: channelMatch[1] };
  }

  // 6. Post with optional comment anchor: /p/[id]#comment-[commentId]
  const postMatch = pathname.match(/^\/p\/([^/]+)$/);
  if (postMatch) {
    const commentMatch = hash.match(/^#comment-([a-f0-9-]+)$/i);
    if (commentMatch) {
      return { type: 'comment', identifier: commentMatch[1] };
    }
    return { type: 'post', identifier: postMatch[1] };
  }

  // 7. Workspace page: /workspace/[id]. Resolver honours visibility — a
  //    private workspace returns 404 so this only ever attributes for
  //    public/unlisted canvases (matches the share-link policy).
  const workspaceMatch = pathname.match(/^\/workspace\/([^/]+)$/);
  if (workspaceMatch) {
    return { type: 'workspace', identifier: workspaceMatch[1] };
  }

  return null;
}

/* ------------------------------------------------------------------ */
/*  Attribution touch capture                                          */
/* ------------------------------------------------------------------ */

/**
 * Capture first-touch + last-touch attribution as `user_interactions` rows
 * via the existing tracker. UTM params, the document.referrer, and the
 * landing pathname all travel in `metadata`; the targetId is the inferred
 * traffic source ("twitter" / utm_source / "direct").
 *
 * Fires per (anon visitor × session-tagged-visit), so the row volume is
 * bounded — at most one first-touch ever per anon, plus one last-touch
 * per campaign-tagged page load.
 */
function captureAttributionTouch(pathname: string | null, searchParams: URLSearchParams): void {
  if (!tracker) return;

  // Build UTM bag + ad-click + referrer + landing path.
  const utm: Record<string, string> = {};
  for (const k of UTM_KEYS) {
    const v = searchParams.get(k);
    if (v) utm[k] = v;
  }
  const adClick = AD_CLICK_PARAMS.find((p) => searchParams.get(p));
  const adClickValue = adClick ? searchParams.get(adClick) ?? null : null;

  const hasUtm = Object.keys(utm).length > 0;
  const hasAdClick = !!adClick;
  const hasRefcode = !!(searchParams.get('ref') || searchParams.get('referral') || searchParams.get('refcode'));
  const isCampaignTagged = hasUtm || hasAdClick || hasRefcode;

  // First-touch: write at most once per anon, regardless of campaign tagging
  // (landing-on-direct is itself an attribution data point).
  let firstTouchAlreadyFired = false;
  try {
    firstTouchAlreadyFired = !!localStorage.getItem(FIRST_TOUCH_SENTINEL);
  } catch {
    // localStorage unavailable — fail open and write the touch (worst case is
    // a duplicate row, not a missing one).
  }

  const referrer = typeof document !== 'undefined' ? document.referrer : '';
  const landingPath = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  const source = utm.utm_source
    || (referrer ? safeReferrerHost(referrer) : null)
    || (hasAdClick ? 'google_ads' : null)
    || 'direct';

  const baseMeta = {
    utmSource: utm.utm_source,
    utmMedium: utm.utm_medium,
    utmCampaign: utm.utm_campaign,
    utmTerm: utm.utm_term,
    utmContent: utm.utm_content,
    referrer: referrer || undefined,
    landingPath,
    refCode: searchParams.get('ref') || searchParams.get('referral') || searchParams.get('refcode') || undefined,
    adClick: adClick && adClickValue ? { param: adClick, value: adClickValue } : null,
  };

  if (!firstTouchAlreadyFired) {
    tracker.trackAttribution('first', source, baseMeta);
    try { localStorage.setItem(FIRST_TOUCH_SENTINEL, '1'); } catch { /* ignore */ }
  }

  if (isCampaignTagged) {
    tracker.trackAttribution('last', source, baseMeta);
  }
}

function safeReferrerHost(referrer: string): string | null {
  try {
    const u = new URL(referrer);
    if (typeof window !== 'undefined' && u.host === window.location.host) return null; // internal nav
    return u.host;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * ReferralCapture
 *
 * Runs on every page load to detect and store referral codes using
 * **last-touch** attribution with a 30-day window.
 *
 * Tracked surfaces:
 *   - /ref/[code]              (direct link)
 *   - ?ref=[code]              (query parameter)
 *   - /u/[username]            (user / agent profile)
 *   - /c/[slug]                (channel page)
 *   - /p/[id]                  (post page)
 *   - /p/[id]#comment-[uuid]   (comment anchor)
 *   - /content/[id]            (content page, `?t=` share token wins)
 *   - /w/[idOrCode], /wp/[..]  (standalone widget viewer / share links)
 *   - /workspace/[id]          (public/unlisted canvas)
 *
 * Uses both localStorage (primary, survives browser restart) and a
 * cookie (backup, readable by server-side auth callbacks).
 *
 * Non-essential attribution storage (referralCode / rb_gclid cookies + LS
 * mirrors) is consent-gated: values captured before the cookie choice are
 * staged in sessionStorage and promoted on acceptance, or discarded on
 * decline. First-touch/UTM analytics events ride the tracker unchanged.
 */
export function ReferralCapture() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { user, loading } = useAuth();
  const { consent } = useConsent();

  // Consent lifecycle for staged captures. The useConsent hook state is
  // per-instance (the cookie banner runs its own instance), so in addition to
  // reacting to this hook's value we poll the consent cookie while the choice
  // is undecided, and promote or clear staged values the moment it resolves.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (consent === 'accepted') {
      promoteStagedAttribution();
      return;
    }
    if (consent === 'rejected') {
      clearStagedAttribution();
      return;
    }

    // Undecided ('pending' before hydration, or null = no choice yet): watch
    // the consent cookie so a decision made in the cookie banner (a separate
    // hook instance) is picked up without a navigation. This is only a
    // BACKSTOP: the banner calls promoteStagedAttribution() directly on
    // accept, so promotion normally happens in the same click. The interval
    // is short so any other path that sets consent (a second tab, a legacy
    // caller) still promotes before the visitor can navigate away.
    const interval = window.setInterval(() => {
      const current = getAnalyticsConsent();
      if (current === 'accepted') {
        promoteStagedAttribution();
        window.clearInterval(interval);
      } else if (current === 'rejected') {
        clearStagedAttribution();
        window.clearInterval(interval);
      }
    }, CONSENT_POLL_MS);
    return () => window.clearInterval(interval);
  }, [consent, pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (loading) return;

    // Capture Google Ads click IDs for all users (returning visitors click ads too)
    captureAdClickParams(searchParams);

    // Capture attribution touches (UTMs / referrer / landing path).
    // Fires for both anonymous and authenticated visitors — landing context is
    // useful even for return users (e.g. tracking a re-engagement campaign).
    captureAttributionTouch(pathname, searchParams);

    // Skip referral capture if user is already logged in — referrals only apply at signup
    if (user) return;

    const hash = window.location.hash;
    const referralInfo = extractReferralInfo(pathname, searchParams, hash);
    if (!referralInfo) return;

    // Handle direct referral codes immediately (last-touch: always overwrite)
    if (referralInfo.type === 'direct') {
      storeReferral(referralInfo.identifier);
      return;
    }

    // Resolve content to referral code via API, then store (last-touch)
    const resolveAndStore = async () => {
      try {
        const response = await fetch(
          `/api/referrals/resolve?type=${referralInfo.type}&id=${encodeURIComponent(referralInfo.identifier)}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.code) {
            storeReferral(data.code);
          }
        }
      } catch {
        // Silently fail — referral tracking is not critical
      }
    };

    resolveAndStore();
  }, [pathname, searchParams, user, loading]);

  return null;
}

/* ------------------------------------------------------------------ */
/*  Public helpers for signup flows                                     */
/* ------------------------------------------------------------------ */

/**
 * Get the stored referral code (checks localStorage first, then cookie).
 * Used during signup / auth callback flows.
 */
export function getReferralCodeFromCookie(request?: Request): string | null {
  // Server-side: parse from request cookies
  if (request) {
    const cookieHeader = request.headers.get('cookie') || '';
    const match = cookieHeader.match(new RegExp(`(^|; )${COOKIE_NAME}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : null;
  }

  // Client-side: prefer localStorage, fall back to cookie
  if (typeof window === 'undefined') return null;
  return getStoredReferral() || getCookie(COOKIE_NAME);
}

/**
 * Clear the stored referral code.
 * Called after successful referral application during signup.
 */
export function clearReferralCookie(): void {
  if (typeof window === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
  try {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem(LS_TS_KEY);
  } catch {
    // ignore
  }
}
